import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Target, Sparkle, TrendUp, ShieldCheck, ArrowRight, GitMerge, Moon } from "@phosphor-icons/react";
import { ConsultButton } from "@/components/ConsultCTA";
import api from "@/lib/api";

export default function MyProfile({ onNavigate }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/profile/synthesis").then((r) => setData(r.data)).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex justify-center py-32"><div className="h-9 w-9 animate-spin rounded-full border-2 border-slate-800 border-t-teal-400" /></div>;

  const growth = (data.patterns || []).filter((p) => p.polarity === "growth");
  const strengths = data.strengths || [];

  return (
    <div data-testid="my-profile">
      <p className="text-xs font-bold uppercase tracking-[0.2em] text-teal-500">Your living profile</p>
      <h1 className="mt-2 font-heading text-3xl font-light tracking-tight text-slate-50 sm:text-4xl">The patterns that connect you</h1>
      <p className="mt-2 max-w-2xl text-sm text-slate-400">Every assessment you take adds a layer. This is the one coherent picture forming underneath them all.</p>

      {/* Completeness */}
      <div className="mt-8 rounded-2xl border border-slate-800 bg-slate-900 p-6" data-testid="profile-completeness">
        <div className="flex items-center justify-between">
          <h3 className="font-heading text-base font-medium text-slate-100">Profile depth</h3>
          <span className="font-heading text-2xl font-semibold text-teal-300">{data.completeness}%</span>
        </div>
        <div className="mt-3 h-2.5 overflow-hidden rounded-full bg-slate-800">
          <motion.div initial={{ width: 0 }} animate={{ width: `${data.completeness}%` }} transition={{ duration: 0.8 }} className="h-full rounded-full bg-gradient-to-r from-teal-500 to-sky-400" />
        </div>
        <p className="mt-3 text-sm text-slate-500">{data.assessments_done} of {data.assessments_total} assessments mapped{data.has_symbolic ? " · symbolic layer added" : ""}. The more you map, the more the platform understands you.</p>
      </div>

      {data.assessments_done === 0 ? (
        <div className="mt-6 rounded-2xl border border-dashed border-slate-800 p-12 text-center" data-testid="profile-empty">
          <Sparkle size={30} weight="duotone" className="mx-auto text-slate-600" />
          <h2 className="mt-4 font-heading text-xl font-medium text-slate-100">Your map is empty — for now</h2>
          <p className="mx-auto mt-2 max-w-md text-sm text-slate-400">Take your first assessment and your living profile will start to form.</p>
          <button onClick={() => onNavigate("assessments")} className="mt-5 inline-flex items-center gap-2 rounded-full bg-teal-500 px-6 py-3 text-sm font-semibold text-slate-950 hover:bg-teal-400">Browse assessments</button>
        </div>
      ) : (
        <>
          {/* Top growth edge */}
          {data.top_growth_edge && (
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="mt-6 rounded-2xl border border-amber-500/30 bg-gradient-to-br from-slate-900 to-slate-950 p-7" data-testid="profile-growth-edge">
              <div className="flex items-center gap-2"><TrendUp size={16} weight="duotone" className="text-amber-400" /><p className="text-xs font-bold uppercase tracking-[0.2em] text-amber-500">Your strongest growth edge</p></div>
              <h3 className="mt-3 font-heading text-2xl font-light text-amber-200">{data.top_growth_edge.name}</h3>
              <p className="mt-2 max-w-xl text-sm leading-relaxed text-slate-300">{data.top_growth_edge.blurb}</p>
              <p className="mt-3 text-sm text-slate-400"><span className="text-amber-400">Try this:</span> {data.top_growth_edge.growth}</p>
            </motion.div>
          )}

          {/* Agreement */}
          {data.agreement.length > 0 && (
            <div className="mt-6 rounded-2xl border border-teal-500/30 bg-slate-900 p-6" data-testid="profile-agreement">
              <div className="flex items-center gap-2"><GitMerge size={16} weight="duotone" className="text-teal-400" /><h3 className="font-heading text-base font-medium text-slate-100">Where your assessments agree</h3></div>
              <p className="mt-1 text-sm text-slate-500">These patterns showed up across more than one assessment — usually the root worth working on first.</p>
              <div className="mt-4 space-y-3">
                {data.agreement.map((p) => (
                  <div key={p.id} className="rounded-xl border border-slate-800 bg-slate-950/50 p-4" data-testid={`agree-${p.id}`}>
                    <div className="flex items-center justify-between">
                      <span className="font-heading text-base font-medium text-teal-200">{p.name}</span>
                      <span className="rounded-full bg-teal-500/15 px-2.5 py-0.5 text-xs text-teal-300">seen in {p.count}</span>
                    </div>
                    <p className="mt-1.5 text-sm text-slate-400">{p.blurb}</p>
                    <p className="mt-1 text-xs text-slate-600">From: {p.sources.join(" · ")}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Recurring patterns */}
          <div className="mt-6">
            <h3 className="flex items-center gap-2 font-heading text-lg font-medium text-slate-200"><Target size={18} weight="duotone" className="text-teal-400" /> Your recurring patterns</h3>
            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              {growth.map((p) => (
                <div key={p.id} className="rounded-2xl border border-slate-800 bg-slate-900 p-5" data-testid={`pattern-${p.id}`}>
                  <div className="flex items-center justify-between">
                    <span className="font-heading text-base font-medium text-slate-100">{p.name}</span>
                    <span className="font-mono text-xs text-slate-500">{p.count}×</span>
                  </div>
                  <p className="mt-1.5 text-sm leading-relaxed text-slate-400">{p.blurb}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Strengths */}
          {strengths.length > 0 && (
            <div className="mt-6 rounded-2xl border border-slate-800 bg-slate-900 p-6" data-testid="profile-strengths">
              <div className="flex items-center gap-2"><ShieldCheck size={16} weight="duotone" className="text-emerald-400" /><h3 className="font-heading text-base font-medium text-slate-100">What's stable in you</h3></div>
              <div className="mt-3 flex flex-wrap gap-2">
                {strengths.map((p) => <span key={p.id} className="rounded-full border border-emerald-500/30 bg-emerald-500/10 px-3 py-1 text-sm text-emerald-300">{p.name}</span>)}
              </div>
            </div>
          )}

          {/* Contextual consult prompt (v2) */}
          {data.consult_prompt && (
            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="mt-6 rounded-2xl border border-amber-500/30 bg-gradient-to-br from-slate-900 to-slate-950 p-6" data-testid="profile-consult-prompt">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-amber-500">Go deeper with Avnni</p>
              <p className="mt-2 max-w-2xl text-sm leading-relaxed text-slate-300">{data.consult_prompt.message}</p>
              <div className="mt-4"><ConsultButton source={`profile-${data.consult_prompt.reason}`} /></div>
            </motion.div>
          )}

          {/* Cross-links */}
          <div className="mt-6 flex flex-wrap items-center gap-3">
            <button onClick={() => onNavigate("assessments")} className="inline-flex items-center gap-2 rounded-full bg-teal-500 px-5 py-2.5 text-sm font-semibold text-slate-950 hover:bg-teal-400" data-testid="profile-take-more">Map more of yourself <ArrowRight size={15} weight="bold" /></button>
            <button onClick={() => onNavigate("symbolic")} className="inline-flex items-center gap-2 rounded-full border border-indigo-500/40 px-5 py-2.5 text-sm font-medium text-indigo-300 hover:bg-indigo-500/10" data-testid="profile-to-symbolic"><Moon size={15} weight="duotone" /> Add the symbolic layer</button>
          </div>
        </>
      )}
    </div>
  );
}
